/**
 * How app records map to the workbook (docs/excel-integration.md §5, decisions D2/D3/D6).
 * Pure: used when building sync packages; the Excel helper (a later step) only applies them.
 */
import { excelLedgerDate, parseAccountingMonth } from "./accounting-period";
import { workbookIncomeLine, type WorkbookIncomeLine } from "./income";
import type { Expense, Income } from "./model";
import { round2 } from "./money";

/** One row of `Table388` after the planned migration (columns A–J). `Mese`/`ISBLANK` are formulas. */
export interface ExcelLedgerRow {
  /** Accounting date: purchase date, or the 1st of the accounting month when deferred (D3). */
  Data: string;
  "Tipo Spesa": string;
  Importo: string;
  "Metodo Pagamento": string;
  Note: string;
  "Data Acquisto": string;
  Esercente: string;
  "ID App": string;
}

export interface ExcelLedgerOperation {
  id: string;
  version: number;
  op: "upsert" | "remove";
  /** One workbook per accounting year (D6): a December card purchase goes to next year's file. */
  workbookYear: number;
  row: ExcelLedgerRow | null;
}

/** For a later phase: mirroring the income ledger into the salary cell / Extra Entrate list (Q3). */
export interface ExcelIncomeOperation {
  id: string;
  version: number;
  op: "upsert" | "remove";
  workbookYear: number;
  /** 1–12: the monthly sheet (Gennaio … Dicembre). */
  month: number;
  line: WorkbookIncomeLine;
  description: string;
  amount: string;
}

export function projectExpense(expense: Expense, categoryLabel: string, paymentMethodLabel: string): ExcelLedgerOperation {
  const { year } = parseAccountingMonth(expense.accountingMonth);
  if (expense.status !== "confirmed") return { id: expense.id, version: expense.version, op: "remove", workbookYear: year, row: null };
  return {
    id: expense.id,
    version: expense.version,
    op: "upsert",
    workbookYear: year,
    row: {
      Data: excelLedgerDate(expense.purchaseDate, expense.accountingMonth),
      "Tipo Spesa": categoryLabel,
      Importo: round2(expense.amountEur).toFixed(2),
      "Metodo Pagamento": paymentMethodLabel,
      Note: expense.note ?? "",
      "Data Acquisto": expense.purchaseDate,
      Esercente: expense.merchant ?? "",
      "ID App": expense.id,
    },
  };
}

export function projectIncome(income: Income): ExcelIncomeOperation {
  const { year, month } = parseAccountingMonth(income.accountingMonth);
  return {
    id: income.id,
    version: income.version,
    op: income.status === "confirmed" ? "upsert" : "remove",
    workbookYear: year,
    month,
    line: workbookIncomeLine(income.type),
    description: income.description,
    amount: round2(income.amount).toFixed(2),
  };
}
